import { phaseLabel } from "@/lib/phase";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, ReferenceLine, Area, ComposedChart } from "recharts";
import { CheckCircle2, AlertTriangle, Lock, Sparkles } from "lucide-react";
import BodyMetrics from "@/components/BodyMetrics";
import { RebuildSparkline } from "@/components/RebuildSparkline";
import { ProgressPhotos } from "@/components/ProgressPhotos";
import { ReviewComparison } from "@/components/ReviewComparison";

const today = () => new Date().toISOString().slice(0, 10);

const VERDICTS = {
  on_track:    { label: "You are on track.",    tone: "bg-primary text-primary-foreground",  icon: CheckCircle2, sub: "Keep the rhythm. Small wins compound." },
  off_track:   { label: "You are off pace.",    tone: "bg-foreground text-background",       icon: AlertTriangle, sub: "Return, don't restart. The rule: never miss twice." },
  just_started:{ label: "You've just started.", tone: "bg-card border border-border",        icon: Sparkles,     sub: "First week logs will set your baseline." },
  not_started: { label: "Rebuild not started.", tone: "bg-card border border-border",        icon: Sparkles,     sub: "Complete onboarding to unlock your 12-week arc." },
};

const BAND_COLOR = {
  "BUILT":    "text-primary",
  "ON TRACK": "text-primary",
  "REBUILD":  "text-foreground",
  "RESET":    "text-destructive",
  "—":        "text-muted-foreground",
};

export default function Track() {
  const [history, setHistory] = useState(null);
  const [logs, setLogs] = useState([]);
  const [eat, setEat] = useState(null);
  const [form, setForm] = useState({ date: today(), steps: "", workout_done: false, nutrition_score: 0, sleep_hours: "", weight_kg: "", waist_cm: "", notes: "", whole_food_target_met: null, balanced_plate_target_met: null, portion_awareness_met: null, default_day_met: null, flexibility_intentional: null });

  const load = async () => {
    try {
      const [h, l, d] = await Promise.all([api.get("/track/rebuild-history"), api.get("/track/logs"), api.get("/dashboard")]);
      setHistory(h.data); setLogs(l.data); setEat(d.data.eat);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        date: form.date,
        steps: Number(form.steps) || 0,
        workout_done: !!form.workout_done,
        nutrition_score: Number(form.nutrition_score) || 0,
        sleep_hours: Number(form.sleep_hours) || 0,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
        waist_cm: form.waist_cm ? Number(form.waist_cm) : null,
        notes: form.notes,
      };
      // Phase-aware EAT check-ins (only send fields the current phase uses)
      (eat?.questions || []).forEach((q) => {
        const v = form[q.key];
        if (v !== null && v !== undefined) payload[q.key] = v;
      });
      await api.post("/track/log", payload);
      toast.success("Logged.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const chartData = useMemo(() => (history?.weeks || []).map((w) => ({
    week: `W${w.week}`,
    Score: w.status === "locked" ? null : w.score,
    Expected: w.expected,
    phase: w.phase,
  })), [history]);

  if (!history) return <div className="text-muted-foreground uppercase tracking-wider text-sm">Loading your 12-week trend…</div>;

  const verdict = VERDICTS[history.verdict] || VERDICTS.just_started;
  const VerdictIcon = verdict.icon;

  return (
    <div className="space-y-8">
      <div>
        <div className="label-eyebrow">Track · The 12-Week Arc</div>
        <h1 className="wordmark mt-3 text-4xl md:text-5xl uppercase">Your Rebuild Trend.</h1>
        <p className="mt-2 text-muted-foreground max-w-xl">Weekly Rebuild Score across the 12-week program. Are you on track or drifting?</p>
      </div>

      {/* Retrospective logging — Log a past day */}
      <form onSubmit={save} className="bg-card border border-border rounded-lg p-6" data-testid="log-past-day">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="label-eyebrow">Log A Day</div>
            <h2 className="wordmark mt-2 text-2xl uppercase">Add or update any day in your Reset.</h2>
            <p className="mt-1 text-xs text-muted-foreground">Missed logging on the day? Pick that date and log it now — your Rebuild Score updates automatically. No data ≠ a failed day.</p>
          </div>
        </div>
        <div className="mt-5 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          <label className="text-xs">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Log for date</span>
            <input type="date" value={form.date} max={today()}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="log-date" />
          </label>
          <label className="text-xs">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Steps</span>
            <input type="number" min={0} value={form.steps} onChange={(e) => setForm({ ...form, steps: e.target.value })}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="log-steps" placeholder="e.g. 8500" />
          </label>
          <label className="text-xs">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Sleep (hours)</span>
            <input type="number" min={0} step={0.5} value={form.sleep_hours}
              onChange={(e) => setForm({ ...form, sleep_hours: e.target.value })}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="log-sleep" placeholder="e.g. 7.5" />
          </label>
          <label className="text-xs">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Plate score (0–3)</span>
            <input type="number" min={0} max={3} value={form.nutrition_score}
              onChange={(e) => setForm({ ...form, nutrition_score: e.target.value })}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="log-plate" />
          </label>
          <label className="text-xs flex items-center gap-2 mt-6">
            <input type="checkbox" checked={form.workout_done}
              onChange={(e) => setForm({ ...form, workout_done: e.target.checked })} data-testid="log-workout" />
            <span className="text-sm">Workout completed this day</span>
          </label>
        </div>

        {/* Phase-aware EAT check-in (BA30 10K30 EAT brief) */}
        {eat && eat.questions?.length > 0 && (
          <div className="mt-6 rounded-md border border-border p-4 bg-muted/30" data-testid="eat-checkin">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="label-eyebrow" style={{ color: eat.colour }}>EAT · {phaseLabel(eat.phase)}</div>
                <div className="mt-1 text-sm font-medium">{eat.standard}</div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Behaviour check-in, not a food diary</div>
            </div>
            <div className="mt-4 space-y-3">
              {eat.questions.map((q) => {
                const options = q.type === "yesnona" ? ["YES", "NO", "N/A"] : ["YES", "NO"];
                const val = form[q.key];
                return (
                  <div key={q.key} className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="text-sm flex-1 min-w-[240px]">{q.label}</div>
                    <div className="flex gap-2" data-testid={`eat-q-${q.key}`}>
                      {options.map((opt) => {
                        const boolVal = q.type === "yesnona" ? opt : opt === "YES";
                        const isSelected = q.type === "yesnona" ? val === opt : val === boolVal;
                        return (
                          <button key={opt} type="button"
                            onClick={() => setForm({ ...form, [q.key]: boolVal })}
                            className={`text-[11px] uppercase tracking-widest px-3 py-1.5 rounded border transition-colors ${isSelected ? "text-white border-transparent" : "border-border text-muted-foreground hover:text-foreground"}`}
                            style={isSelected ? { backgroundColor: eat.colour } : {}}
                            data-testid={`eat-q-${q.key}-${opt.toLowerCase().replace("/", "").replace("a", "a")}`}>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button type="submit" className="btn-primary btn-sm" data-testid="log-save">Save entry</button>
        </div>
      </form>

      {/* v2 Rebuild sparkline — behaviour-adherence trend */}
      <RebuildSparkline />

      {/* Verdict banner */}
      <div className={`rounded-lg p-6 md:p-8 flex items-start gap-5 ${verdict.tone}`} data-testid="verdict-banner">
        <VerdictIcon size={32} strokeWidth={2.25} className={history.verdict === "on_track" ? "text-primary-foreground" : "text-primary"} />
        <div className="flex-1">
          <div className="label-eyebrow opacity-80">Verdict · Week {history.current_week} of 12</div>
          <h2 className="wordmark mt-2 text-3xl md:text-4xl uppercase leading-tight">{verdict.label}</h2>
          <p className="mt-2 text-sm opacity-90">{verdict.sub}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="label-eyebrow opacity-80">On track</div>
          <div className="wordmark text-5xl leading-none" data-testid="on-track-ratio">
            {history.on_track_count}<span className="opacity-60 text-2xl">/{history.total_completed}</span>
          </div>
          <div className="text-[10px] uppercase tracking-widest opacity-70 mt-1">weeks</div>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Avg score" value={history.avg_score || "—"} sub="of 100" />
        <StatTile label="Weeks complete" value={`${history.total_completed}/12`} sub="tracked" />
        <StatTile label="On-track rate" value={history.total_completed ? `${Math.round(history.on_track_count / history.total_completed * 100)}%` : "—"} sub="hit expected" />
        <StatTile label="Current week" value={`${history.current_week}/12`} sub={phaseLabel(history.weeks[history.current_week - 1]?.phase)} />
      </div>

      {/* Trend chart */}
      <div className="bg-card border border-border rounded-lg p-6" data-testid="trend-chart">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="label-eyebrow">Weekly Rebuild Score · Actual vs Expected</div>
          <div className="flex gap-4 text-[11px] uppercase tracking-widest">
            <span className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-sm bg-primary" /> Actual</span>
            <span className="flex items-center gap-2"><span className="inline-block w-3 h-[2px] bg-foreground/60" style={{borderTop:"2px dashed"}} /> Expected</span>
          </div>
        </div>
        <div className="h-72 mt-4">
          <ResponsiveContainer>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="week" fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <YAxis domain={[0, 100]} fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <ReferenceLine y={40} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 4" label={{ value: "RESET →", fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
              <ReferenceLine y={65} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 4" label={{ value: "ON TRACK →", fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
              <ReferenceLine y={85} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 4" label={{ value: "BUILT →", fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
              <Line type="monotone" dataKey="Expected" stroke="hsl(var(--foreground))" strokeWidth={1.5} strokeDasharray="6 4" dot={false} />
              <Line type="monotone" dataKey="Score" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ r: 4, fill: "hsl(var(--primary))" }} connectNulls={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Weekly breakdown */}
      <div>
        <div className="label-eyebrow mb-4">Week-by-Week Breakdown</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {history.weeks.map((w) => {
            const isLocked = w.status === "locked";
            const isCurrent = w.status === "current";
            return (
              <div key={w.week}
                className={`rounded-lg p-5 border ${isLocked ? "bg-muted/40 border-border" : isCurrent ? "bg-foreground text-background border-foreground" : w.on_track ? "bg-card border-primary/40" : "bg-card border-border"}`}
                data-testid={`week-${w.week}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className={`label-eyebrow ${isCurrent ? "!text-background/70" : ""}`}>Week {w.week} · {phaseLabel(w.phase)}</div>
                    {!isLocked && (
                      <div className={`mt-2 wordmark text-5xl leading-none ${isCurrent ? "text-primary" : "text-foreground"}`}>
                        {w.score}<span className={`text-2xl ${isCurrent ? "text-background/50" : "text-foreground/40"}`}>/100</span>
                      </div>
                    )}
                    {isLocked && <div className="mt-2 wordmark text-5xl leading-none text-muted-foreground">—</div>}
                  </div>
                  {isLocked ? <Lock size={16} className="text-muted-foreground" />
                    : w.on_track ? <CheckCircle2 size={18} className="text-primary" />
                    : <AlertTriangle size={18} className={isCurrent ? "text-primary" : "text-foreground/60"} />}
                </div>
                {!isLocked && (
                  <>
                    <div className={`mt-3 font-head uppercase text-xs tracking-widest ${isCurrent ? "text-primary" : BAND_COLOR[w.band]}`}>{w.band}</div>
                    <div className={`text-[10px] uppercase tracking-widest mt-1 ${isCurrent ? "text-background/70" : "text-muted-foreground"}`}>
                      Expected {w.expected} · {w.on_track ? "On track" : "Behind pace"}
                    </div>
                    <div className={`mt-4 pt-3 border-t ${isCurrent ? "border-background/20" : "border-border"} grid grid-cols-4 gap-1 text-center`}>
                      <MiniStat label="Steps" v={w.avg_steps ? Math.round(w.avg_steps / 100) / 10 + "K" : "0"} on={isCurrent} />
                      <MiniStat label="Lifts" v={w.workouts} on={isCurrent} />
                      <MiniStat label="Plate" v={`${w.nutrition_avg}/3`} on={isCurrent} />
                      <MiniStat label="Sleep" v={`${w.sleep_avg}h`} on={isCurrent} />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Foundation reviews side by side */}
      <ReviewComparison />

      {/* Progress Photos — monthly */}
      <ProgressPhotos />

      {/* Body Metrics · monthly */}
      <BodyMetrics />
    </div>
  );
}

function StatTile({ label, value, sub }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="label-eyebrow">{label}</div>
      <div className="wordmark text-4xl mt-2 leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}

function MiniStat({ label, v, on }) {
  return (
    <div>
      <div className={`text-[9px] uppercase tracking-widest ${on ? "text-background/60" : "text-muted-foreground"}`}>{label}</div>
      <div className={`text-xs font-semibold mt-0.5 ${on ? "text-background" : "text-foreground"}`}>{v}</div>
    </div>
  );
}
